import { useEffect, useState } from "react";
import { api, imgSrc } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { useCurrency } from "@/lib/currency";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { toast } from "sonner";
import { Check, Upload } from "lucide-react";

export default function Profile() {
    const { user, setUser } = useAuth();
    const { t } = useT();
    const { format: formatMoney } = useCurrency();
    const [form, setForm] = useState({ name: user?.name || "", phone: user?.phone || "", picture: user?.picture || "" });
    const [saving, setSaving] = useState(false);
    const [uploadingPic, setUploadingPic] = useState(false);
    const [loyalty, setLoyalty] = useState(null);

    useEffect(() => {
        api.get("/profile/loyalty").then((r) => setLoyalty(r.data)).catch(() => {});
    }, []);

    useEffect(() => {
        if (user) setForm({ name: user.name || "", phone: user.phone || "", picture: user.picture || "" });
    }, [user]);

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const res = await api.patch("/profile", form);
            setUser(res.data);
            toast.success(t("toast.saved"));
        } catch (err) {
            toast.error(err.response?.data?.detail || t("toast.saveFailed"));
        } finally {
            setSaving(false);
        }
    };

    const uploadAvatar = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (!file.type.startsWith("image/")) { toast.error(t("profile.notAnImage")); return; }
        setUploadingPic(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const res = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const next = { ...form, picture: res.data.url };
            setForm(next);
            const saved = await api.patch("/profile", next);
            setUser(saved.data);
            toast.success(t("toast.saved"));
        } catch (err) {
            toast.error(err.response?.data?.detail || t("toast.saveFailed"));
        } finally {
            setUploadingPic(false);
        }
    };

    return (
        <div data-testid="profile-page" className="bg-bone min-h-screen">
            <Navbar />
            <section className="pt-32 lg:pt-40 pb-24 px-6 lg:px-12">
                <div className="max-w-[1300px] mx-auto">
                    <div className="mb-12">
                        <div className="eyebrow mb-4">{t("profile.eyebrow")}</div>
                        <h1 className="font-chiller text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                            {t("profile.title")}
                        </h1>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                        {/* Personal */}
                        <form onSubmit={save} className="lg:col-span-5 border border-espresso/15 p-8 bg-bone2/30" data-testid="profile-form">
                            <div className="eyebrow mb-4">{t("profile.personal")}</div>
                            <div className="space-y-5">
                                <div data-testid="profile-avatar-block">
                                    <label className="eyebrow block mb-2">{t("profile.picture")}</label>
                                    <div className="flex items-center gap-4">
                                        <span className="w-16 h-16 rounded-full bg-forest text-bone flex items-center justify-center text-xl font-serif overflow-hidden shrink-0" data-testid="profile-avatar-preview">
                                            {form.picture ? <img src={imgSrc(form.picture)} alt="" className="w-full h-full object-cover" /> : (form.name?.[0]?.toUpperCase() || "G")}
                                        </span>
                                        <label className="border border-dashed border-espresso/30 bg-bone2/40 px-4 py-3 flex items-center gap-2 cursor-pointer hover:bg-bone2 transition-colors text-xs uppercase tracking-widest2 text-espresso" data-testid="profile-avatar-dropzone">
                                            <input type="file" accept="image/png,image/jpeg,image/webp,image/avif" onChange={uploadAvatar} className="hidden" data-testid="profile-avatar-input" disabled={uploadingPic} />
                                            <Upload size={15} className="text-forest" />
                                            {uploadingPic ? t("profile.uploading") : t("profile.uploadPhoto")}
                                        </label>
                                    </div>
                                </div>
                                <Field label={t("profile.name")} value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="profile-name" />
                                <Field label={t("profile.email")} value={user?.email || ""} disabled testid="profile-email" />
                                <Field label={t("profile.phone")} value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="profile-phone" />
                            </div>
                            <button
                                type="submit"
                                disabled={saving}
                                data-testid="profile-save"
                                className="mt-8 w-full px-6 py-3 bg-forest text-bone hover:bg-forestDark text-xs uppercase tracking-widest2 font-semibold disabled:opacity-50"
                            >
                                {saving ? t("profile.saving") : t("profile.save")}
                            </button>
                        </form>

                        {/* Loyalty */}
                        <div className="lg:col-span-7" data-testid="loyalty-card">
                            <div className="border border-terracotta/40 bg-terracotta/5 p-8 mb-8">
                                <div className="eyebrow text-terracotta mb-3">{t("profile.currentTier")}</div>
                                <div className="flex items-end justify-between flex-wrap gap-4">
                                    <h2 className="font-serif text-5xl lg:text-6xl text-forest" data-testid="loyalty-tier-name">
                                        {loyalty?.tier?.name || "—"}
                                    </h2>
                                    <div className="text-right">
                                        <div className="font-serif text-3xl text-forest" data-testid="loyalty-nights">{loyalty?.total_nights ?? 0}</div>
                                        <div className="eyebrow">{t("profile.totalNights")}</div>
                                    </div>
                                </div>
                                {loyalty?.next_tier && (
                                    <p className="mt-4 text-sm text-espresso" data-testid="loyalty-progress">
                                        {t("profile.nightsToNext", loyalty.nights_to_next_tier, loyalty.next_tier.name)}
                                    </p>
                                )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                                <Stat label={t("profile.totalStays")} value={loyalty?.completed_stays ?? 0} />
                                <Stat label={t("profile.totalSpent")} value={formatMoney(loyalty?.total_spent ?? 0)} />
                            </div>

                            <div className="border border-espresso/15 p-8 mb-8">
                                <div className="eyebrow mb-3">{t("profile.perks")}</div>
                                <ul className="space-y-3">
                                    {(loyalty?.tier?.perks || []).map((p) => (
                                        <li key={p} className="flex items-center gap-3 text-sm text-ink/85"><Check size={14} className="text-terracotta" /> {p}</li>
                                    ))}
                                </ul>
                            </div>

                            <div className="border border-espresso/15 p-8">
                                <div className="eyebrow mb-4">{t("profile.allTiers")}</div>
                                <div className="space-y-4">
                                    {(loyalty?.all_tiers || []).map((tier) => {
                                        const current = loyalty?.tier?.key === tier.key;
                                        return (
                                            <div key={tier.key} className={`flex items-start justify-between gap-4 ${current ? "" : "opacity-70"}`} data-testid={`tier-${tier.key}`}>
                                                <div>
                                                    <div className="font-serif text-2xl flex items-center gap-3">
                                                        {tier.name}
                                                        {current && <span className="eyebrow text-terracotta">— {t("profile.current")}</span>}
                                                    </div>
                                                    <div className="text-xs text-muted2 mt-1">{tier.min_nights}+ {t("common.nights")}</div>
                                                </div>
                                                <ul className="text-xs text-muted2 text-right space-y-1 max-w-[55%]">
                                                    {tier.perks.map((p) => <li key={p}>{p}</li>)}
                                                </ul>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <Footer />
        </div>
    );
}

function Field({ label, value, onChange, testid, disabled }) {
    return (
        <div>
            <label className="eyebrow block mb-2">{label}</label>
            <input
                data-testid={testid}
                type="text"
                value={value}
                disabled={disabled}
                onChange={onChange ? (e) => onChange(e.target.value) : undefined}
                className={`w-full bg-transparent border-b border-espresso/30 ${disabled ? "text-muted2" : "focus:border-forest"} outline-none py-3`}
            />
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="border border-espresso/15 p-6">
            <div className="eyebrow mb-2">{label}</div>
            <div className="font-serif text-3xl">{value}</div>
        </div>
    );
}
